
import { supabase } from "@/integrations/supabase/client";

// Helper function to get photos from either column
const getPhotoUrls = (profile: any): string[] => {
  // Prioritize photo_urls, fall back to photos
  if (profile.photo_urls && profile.photo_urls.length > 0) {
    return profile.photo_urls;
  }
  if (profile.photos && profile.photos.length > 0) {
    return profile.photos;
  }
  return [];
};

// Helper function to filter sensitive data for public viewing
const filterPublicProfileData = (profile: any) => {
  if (!profile) return null;
  
  // Only include data that should be publicly visible
  return {
    id: profile.id,
    user_id: profile.user_id,
    display_name: profile.display_name,
    age: profile.age,
    bio: profile.bio,
    location: profile.location,
    photo_urls: getPhotoUrls(profile),
    public_interests: profile.public_interests || [],
    // Explicitly exclude private data
    // private_interests, subscription_tier, etc. are not included
  };
};

export const profileService = {
  async loadExistingProfile(userId: string) {
    if (!userId || typeof userId !== 'string') {
      console.error('Invalid user ID provided to loadExistingProfile');
      return null;
    }

    console.log('🔍 Attempting to load profile for user:', userId);

    const { data: existingProfile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle(); // Use maybeSingle instead of single to handle missing profiles

    if (error) {
      console.error('❌ Error loading existing profile:', error.message);
      throw error;
    }

    if (!existingProfile) {
      console.log('ℹ️ No profile found for user - this is normal for new users');
      return null;
    }

    // Normalize photo URLs
    const photoUrls = getPhotoUrls(existingProfile);
    existingProfile.photo_urls = photoUrls;

    console.log('✅ Profile loaded successfully');
    return existingProfile;
  },

  async loadPublicProfile(userId: string) {
    if (!userId || typeof userId !== 'string') {
      console.error('Invalid user ID provided to loadPublicProfile');
      return null;
    }

    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select(`
        id,
        user_id,
        display_name,
        age,
        bio,
        location,
        photo_urls,
        photos,
        public_interests
      `)
      .eq('user_id', userId)
      .eq('is_visible', true)
      .maybeSingle(); // Use maybeSingle instead of single

    if (error) {
      console.error('Error loading public profile:', error.message);
      return null;
    }

    return filterPublicProfileData(profile);
  },

  async updateProfile(userId: string, updateData: any) {
    if (!userId || typeof userId !== 'string') {
      throw new Error('Invalid user ID');
    }

    // Sanitize update data to prevent unwanted modifications
    const allowedFields = [
      'display_name', 'age', 'bio', 'location', 'photo_urls', 'photos',
      'public_interests', 'private_interests', 'age_range_min', 'age_range_max',
      'max_distance', 'looking_for', 'is_profile_complete', 'first_name', 'last_name',
      'height', 'weight', 'body_type', 'ethnicity', 'relationship_status',
      'show_age', 'position', 'turned_on_by', 'address', 'additional_info', 'city'
    ];

    const sanitizedData: any = {};
    for (const [key, value] of Object.entries(updateData)) {
      if (allowedFields.includes(key)) {
        sanitizedData[key] = value;
      }
    }

    console.log('🔄 Updating profile with sanitized data:', sanitizedData);

    const { data, error } = await supabase
      .from('user_profiles')
      .update(sanitizedData)
      .eq('user_id', userId)
      .select('is_profile_complete')
      .single();

    if (error) {
      console.error('❌ Profile update error:', error.message);
      throw error;
    }

    // Verify the update was actually successful
    if (!data || !data.is_profile_complete) {
      console.error('❌ Profile completion flag not set correctly');
      throw new Error('Profile completion verification failed');
    }

    console.log('✅ Profile updated successfully');
    return data;
  }
};
